import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Input } from '@progress/kendo-react-inputs';
import loading from '../loading.svg';
import { BASEURI, getAuthToken, handleAuthTokenError, advancedFilter } from '../utils';

const WISECONN_SERIAL_PREFIX = 'wc-';
const WISECONN_SEARCHABLE_FIELDS = ['stationName', 'description', 'accountName', 'serial'];

function formatWiseconnSerial(id) {
    return `${WISECONN_SERIAL_PREFIX}${id}`;
}

function normalizeWiseconnStation(station) {
    return {
        ...station,
        stationName: station.name || '',
        accountName: station.account?.name || '',
        serial: formatWiseconnSerial(station.id),
    };
}

function StationItem(props) {
    const [adding, setAdding] = useState(false);
    const intl = useIntl();

    useEffect(() => {
        if (!props.loading)
            setAdding(props.loading);
    }, [props.loading])

    const { station, allWiseconnStations, setAllWiseconnStations } = props;

    const handleAddStation = () => {
        setAdding(true);
        props.addStation({
            keepOpen: true,
            api: 'Wiseconn',
            serial: station.serial,
            name: station.name,
            details: {
                timezone: station.timeZoneName,
                location: {
                    lat: station.latitude,
                    lng: station.longitude
                }
            },
            onSuccess: () => {
                const updatedStations = allWiseconnStations.map(s => {
                    if (s.id === station.id) {
                        return { ...s, is_added: true };
                    }
                    return s;
                });
                setAllWiseconnStations(updatedStations);
                setAdding(false);
            }
        });
    };

    return <tr className="border-b-[1px]">
        <td className="p-2">
            <div>{station.name}</div>
            {station.description &&
                <div className="text-sm font-light text-gray-500">{station.description}</div>}
            {station.account?.name &&
                <div className="text-sm font-light text-gray-500">{station.account.name}</div>}
        </td>
        <td className="p-2">{station.serial}</td>
        <td className="p-2">
            {adding ? <img
                title={intl.formatMessage({ id: 'app.stations.stationForm.adding', defaultMessage: 'Adding station please wait.' })}
                className='p-0 m-0'
                style={{ color: 'gray', width: '31px' }}
                src={loading} alt='loading'></img> :
                <>
                    {!station.is_added ?
                        <button className="text-green-500" onClick={handleAddStation}>
                            <i title={intl.formatMessage({ id: 'app.stations.stationForm.addStation', defaultMessage: 'Add station' })}
                                className="material-icons"
                                style={{ 'color': '#7bd47b' }}>add</i>
                        </button> :
                        <i title={intl.formatMessage({ id: 'app.stations.stationForm.alreadyAded', defaultMessage: 'Already added' })}
                            className="material-icons"
                            style={{ 'color': '#7bd47b' }}>check</i>
                    }
                </>}
        </td>
    </tr>
}

function WiseconnStationsList(props) {
    const intl = useIntl();
    const [allWiseconnStations, setAllWiseconnStations] = useState([]);
    const [searchInput, setSearchInput] = useState('');
    const [loadingDevices, setLoadingDevices] = useState(false);

    useEffect(() => {
        loadWiseconnStations();
    }, []);

    const loadWiseconnStations = () => {
        setLoadingDevices(true);
        let url = `${BASEURI}/api/wiseconn/devices/available/`,
            options = {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                }
            };

        getAuthToken()
            .then(token => token)
            .catch(handleAuthTokenError)
            .then(token => {
                options.headers.Authorization = `Bearer ${token}`;

                fetch(url, options)
                    .then((response) => {
                        if (response.status !== 200 && response.status !== 201) {
                            response.text().then(text => console.error(text));
                            throw new Error(response.statusText);
                        }
                        return response.json();
                    })
                    .then((data) => {
                        setLoadingDevices(false);
                        const stations = (data.stations || []).map(normalizeWiseconnStation);
                        setAllWiseconnStations(stations);
                    })
                    .catch(error => {
                        console.error('Error loading Wiseconn stations:', error);
                        setLoadingDevices(false);
                    });
            });
    };

    const handleSearchInputChange = (event) => {
        setSearchInput(event.target.value);
    };

    let filteredStations = [];
    advancedFilter(
        searchInput,
        allWiseconnStations,
        (items) => { filteredStations = items; },
        WISECONN_SEARCHABLE_FIELDS
    );

    return (
        <div className="mt-4 rounded-md border-[1px] p-0 text-gray-600">
            <div className="border-b-[1px] p-2 uppercase">
                {intl.formatMessage({ id: 'app.stations.stationForm.wiseconnStations', defaultMessage: 'Wiseconn Stations List' })}
            </div>
            <div className="border-b-[1px] p-2">
                <Input
                    placeholder={intl.formatMessage({ id: 'app.stations.search', defaultMessage: 'Search' })}
                    onChange={handleSearchInputChange}
                    style={{ width: '100%' }}
                    value={searchInput}
                />
            </div>
            <table className="m-0 w-full p-0">
                <thead>
                    <tr className="border-b-[1px]">
                        <td className="p-2 capitalize">{intl.formatMessage({ id: 'app.admin.filterMarker.name', defaultMessage: 'Name' })}</td>
                        <td className="p-2 capitalize">{intl.formatMessage({ id: 'app.admin.stationGrid.serial', defaultMessage: 'Serial' })}</td>
                        <td className="p-2 capitalize">{intl.formatMessage({ id: 'app.stations.modelFrom.addStation', defaultMessage: 'Add Station' })}</td>
                    </tr>
                </thead>
                <tbody>
                    {filteredStations.map((station) => (
                        <StationItem
                            key={station.id}
                            addStation={props.addStation}
                            loading={props.loading}
                            station={station}
                            allWiseconnStations={allWiseconnStations}
                            setAllWiseconnStations={setAllWiseconnStations}
                        />
                    ))}
                </tbody>
            </table>
            {loadingDevices ?
                <div className="p-2 text-center text-sm font-light">
                    {intl.formatMessage({ id: 'app.stations.stationForm.loadingStations', defaultMessage: 'Loading Wiseconn Stations...' })}
                </div> :
                <div className="p-2 text-center text-sm font-light">
                    {intl.formatMessage(
                        { id: 'app.stations.stationForm.total', defaultMessage: 'Total {count} Stations' },
                        { count: filteredStations.length }
                    )}
                </div>
            }
        </div>
    )
}

export default WiseconnStationsList;